import { createUniqueFilters } from '@app/src/hooks/queryFilters'
import type { FilterInterface } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { useFiltersContext } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'

interface UseNegatableFilterResult {
  positiveValues: string[]
  negativeValues: string[]
  updateFilters: (positive: string[], negative: string[]) => void
  toggleOptionNegation: (optionId: string) => void
}

const useNegatableFilter = (filterName: string, uniqueId: string): UseNegatableFilterResult => {
  const { filters, setQueryFilters } = useFiltersContext()

  const positiveUniqueId = uniqueId
  const negativeUniqueId = `${uniqueId}-not`

  const filterEntries = filters[filterName] ? [filters[filterName]].flat() : []
  const positiveFilter = filterEntries.find(f => f.uniqueId === positiveUniqueId)
  const negativeFilter = filterEntries.find(f => f.uniqueId === negativeUniqueId)

  const positiveValues = positiveFilter ? ([positiveFilter.value].flat().filter(Boolean) as string[]) : []
  const negativeValues = negativeFilter ? ([negativeFilter.value].flat().filter(Boolean) as string[]) : []

  const updateFilters = (positive: string[], negative: string[]) => {
    const allFilters = Object.values(filters).flat() as FilterInterface[]
    let newFilters = createUniqueFilters(allFilters, filterName, positive, Operators.In, positiveUniqueId, true)
    newFilters = createUniqueFilters(newFilters, filterName, negative, Operators.NotIn, negativeUniqueId, true)
    setQueryFilters(newFilters.filter(Boolean))
  }

  const toggleOptionNegation = (optionId: string) => {
    if (positiveValues.includes(optionId)) {
      updateFilters(
        positiveValues.filter(v => v !== optionId),
        [...negativeValues, optionId],
      )
    } else if (negativeValues.includes(optionId)) {
      updateFilters(
        [...positiveValues, optionId],
        negativeValues.filter(v => v !== optionId),
      )
    }
  }

  return { positiveValues, negativeValues, updateFilters, toggleOptionNegation }
}

export default useNegatableFilter
